import { AdvancedMarker, APIProvider, Map, useMapsLibrary } from "@vis.gl/react-google-maps"
import { useEffect, useRef, useState } from "react"
import supabase from "../supabaseconfig"
import { Recommendation } from "../types/supabase"
import { AnbefalingType } from "./utils"
import style from "./anbefalinger.module.css"

const DEFAULT_CENTER = { lat: 36.7479, lng: -3.8769 } // Nerja, Spain

const kategoriPinFarger: Record<string, { bg: string; border: string }> = {
    RESTAURANT: { bg: "#f1d9c5", border: "#8e4a2e" },
    BAR: { bg: "#e8d6db", border: "#7a3f4f" },
    MUSEUM: { bg: "#dce1ca", border: "#5a6a45" },
    STRAND: { bg: "#f1e3bf", border: "#7e6523" },
    AKTIVITET: { bg: "#d3dec4", border: "#5c7047" },
    ANNET: { bg: "#e8eeec", border: "#3d5a52" },
}

function hentIkon(type: string): string {
    switch (type) {
        case AnbefalingType.RESTAURANT: return "🍽️"
        case AnbefalingType.MUSEUM: return "🏛️"
        case AnbefalingType.BAR: return "🍸"
        case AnbefalingType.AKTIVITET: return "🥾"
        case AnbefalingType.STRAND: return "🏖️"
        case AnbefalingType.ANNET: return "📌"
        default: return "📍"
    }
}

function KategoriPin({ type, onClick }: { type: string; onClick: () => void }) {
    const farge = kategoriPinFarger[type] ?? kategoriPinFarger.ANNET
    return (
        <div
            onClick={onClick}
            style={{
                background: farge.bg,
                border: `2px solid ${farge.border}`,
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                cursor: "pointer",
            }}
        >
            {hentIkon(type)}
        </div>
    )
}

function HjemPin() {
    return (
        <div style={{
            background: "#fff",
            border: "2px solid #3d5a52",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}>
            🏠
        </div>
    )
}

type Pin = {
    recommendation: Recommendation
    lat: number
    lng: number
}

function MapContent({
    recommendations,
    onPinClick,
}: {
    recommendations: Recommendation[]
    onPinClick: (rec: Recommendation) => void
}) {
    const geocodingLib = useMapsLibrary("geocoding")
    const [pins, setPins] = useState<Pin[]>([])
    const [hjemPos, setHjemPos] = useState<{ lat: number; lng: number } | null>(null)
    const cache = useRef<Record<string, { lat: number; lng: number }>>({})

    useEffect(() => {
        if (!geocodingLib) return

        supabase()
            .from("informasjon")
            .select("adresse")
            .single()
            .then(async ({ data }) => {
                if (!data?.adresse) return
                const geocoder = new geocodingLib.Geocoder()
                try {
                    const result = await geocoder.geocode({ address: data.adresse })
                    const loc = result.results[0].geometry.location
                    setHjemPos({ lat: loc.lat(), lng: loc.lng() })
                } catch (err) {
                    console.error("Geocoding av husadresse feilet:", err)
                }
            })
    }, [geocodingLib])

    useEffect(() => {
        if (!geocodingLib) return

        const geocoder = new geocodingLib.Geocoder()
        const withAddress = recommendations.filter((r) => r.address)

        Promise.all(
            withAddress.map(async (r) => {
                const address = r.address!
                if (cache.current[address]) {
                    return { recommendation: r, ...cache.current[address] }
                }
                try {
                    const result = await geocoder.geocode({ address })
                    const loc = result.results[0].geometry.location
                    const coords = { lat: loc.lat(), lng: loc.lng() }
                    cache.current[address] = coords
                    return { recommendation: r, ...coords }
                } catch (err) {
                    console.error(`Geocoding failed for "${address}":`, err)
                    return null
                }
            }),
        ).then((results) => {
            setPins(results.filter(Boolean) as Pin[])
        })
    }, [geocodingLib, recommendations])

    return (
        <>
            {hjemPos && (
                <AdvancedMarker position={hjemPos} title="Residencen">
                    <HjemPin />
                </AdvancedMarker>
            )}
            {pins.map((pin) => (
                <AdvancedMarker
                    key={pin.recommendation.id}
                    position={{ lat: pin.lat, lng: pin.lng }}
                    title={pin.recommendation.name}
                >
                    <KategoriPin
                        type={pin.recommendation.type}
                        onClick={() => onPinClick(pin.recommendation)}
                    />
                </AdvancedMarker>
            ))}
        </>
    )
}

export default function Kartvisning({
    recommendations,
    onPinClick,
}: {
    recommendations: Recommendation[]
    onPinClick: (rec: Recommendation) => void
}) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""

    return (
        <div className={style.kartContainer}>
            <APIProvider apiKey={apiKey}>
                <Map
                    defaultCenter={DEFAULT_CENTER}
                    defaultZoom={14}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID ?? "DEMO_MAP_ID"}
                >
                    <MapContent recommendations={recommendations} onPinClick={onPinClick} />
                </Map>
            </APIProvider>
        </div>
    )
}
